import { useState, useRef } from "react";
import {
  Globe, Languages, ShieldAlert, Smartphone, CreditCard, ArrowRight, ArrowLeft,
  Calendar, Download, ExternalLink, FileText, Image, Film, Palette, ChevronDown, ChevronUp,
  TrendingUp, Store, Zap, Wallet,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { PageFooter } from "@/components/PageFooter";
import { imageSpecs as SHARED_IMAGE_SPECS } from "@/data/assetSpecs";
import { useLang } from "@/i18n";

const benefits = [
  { value: "+22%", label: "Higher avg. order value vs. paper menus", icon: TrendingUp },
  { value: "6,671+", label: "Active outlets across 7 markets", icon: Store },
  { value: "Zero", label: "Reprint costs with real-time menu updates", icon: Zap },
  { value: "100%", label: "Automated allergen compliance", icon: ShieldAlert },
];

const features = [
  { icon: Globe, text: "Fast access — no app required (BYOD)" },
  { icon: Languages, text: "Multilingual / central CMS" },
  { icon: ShieldAlert, text: "Automated allergen tracking" },
  { icon: Smartphone, text: "Responsive design (mobile + tablet)" },
  { icon: CreditCard, text: "Order & Pay integration" },
  { icon: Wallet, text: "Pay at table" },
];

const dependencies = [
  {
    name: "Pay at Table",
    desc:
      "Allows guests to order first and pay later directly at the table by scanning a QR code, without waiting for staff. Includes table-based QR session, real-time bill retrieval from POS, split bills, tip and partial payment support, and payment confirmation synced with POS.",
  },
  {
    name: "Scan & Pay module",
    desc:
      "Enables instant payment via QR scan, either linked to an active table session or as a standalone payment flow. Includes bill preview, secure checkout, and integration with Pay at Table scenarios.",
  },
  {
    name: "POS integration",
    desc:
      "Connects FORA with existing POS systems for seamless real-time data exchange. Includes order sync, bill retrieval, payment reconciliation, and terminal/session mapping.",
  },
  {
    name: "Ordering Panel",
    desc:
      "Digital ordering interface optimized for speed and usability. Includes menu browsing, modifiers and add-ons, direct POS order submission, and mobile/tablet optimized UX.",
  },
  {
    name: "Payless scenarios",
    desc:
      "Advanced frictionless payment experiences designed for premium hospitality journeys. Includes one-click payments, pre-authorized sessions, group payments, and invisible checkout flows.",
  },
  {
    name: "QR Menu \u2014 Implementation (SVC)",
    desc:
      "Service layer for onboarding venues into the system. Includes menu digitization, QR generation, localization, and staff onboarding for fast rollout.",
  },
];
const markets = [{ code: "HR" }, { code: "PL" }, { code: "BA" }, { code: "SI" }, { code: "MK" }, { code: "RS" }, { code: "RO" }];

const themes = [
  { name: "Classic Red", url: "https://qr.master-fb.com/6JC6ES7X", colors: ["#dd260e", "#ffffff"] },
  { name: "Midnight Lounge", url: "https://qr.master-fb.com/V68N9AJW", colors: ["#8b0000", "#000000"] },
  { name: "Vintage Diner", url: "https://qr.master-fb.com/7GZHXPAE", colors: ["#dd260e", "#f5f0e1"] },
  { name: "Eco Refresh", url: "https://qr.master-fb.com/0JZFOZTT", colors: ["#2d8a4e", "#e8f5e9"] },
  { name: "Zero Sugar", url: "https://qr.master-fb.com/DIQH3VER", colors: ["#333333", "#000000"] },
  { name: "Summer Breeze", url: "https://qr.master-fb.com/PMTVONDF", colors: ["#0097a7", "#fff9c4"] },
  { name: "Industrial Loft", url: "https://qr.master-fb.com/DNYYPFH6", colors: ["#555555", "#c8a97e"] },
];

const documents = [
  { name: "Editable Elements in QR App", file: "/editable-elements.pdf", type: "PDF", desc: "Complete guide to all customizable areas: popup ads, banners, client branding, menu categories, product images/videos, and info sections." },
  { name: "QR Themes — Templates", file: "/qr-themes-templates.pdf", type: "PDF", desc: "7 pre-designed Coca-Cola branded theme templates with color palettes, typography, and layout examples." },
  { name: "Requirement — ENG", file: "/requirement-eng.pdf", type: "PDF", desc: "Full assortment requirements: menu structure rules, item descriptions, pricing format, allergen display, and content standards." },
  { name: "Images & Video Instructions", file: "/images-video-instructions.pdf", type: "PDF", desc: "Asset specifications for popup ads (1080×1920), banners (1920×1080), category images, product photos, and video content." },
  { name: "Writing Standards (all markets)", file: "/writing-standards.xlsx", type: "XLSX", desc: "Naming conventions, translation standards, and writing rules for all active markets (HR, PL, SI, BA, MK, RS, RO)." },
];

const imageSpecs = SHARED_IMAGE_SPECS;

export const ProductDetail = ({ onNavigate }) => {
  const { t } = useLang();
  const [implOpen, setImplOpen] = useState(false);
  const [activeDep, setActiveDep] = useState(null);
  const closeTimerRef = useRef(null);

  const openDep = (idx) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setActiveDep(idx);
  };
  const scheduleCloseDep = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setActiveDep(null), 120);
  };

  return (
    <div data-testid="product-detail-view">
      <button onClick={() => onNavigate("home")} className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#555555] hover:text-[#dd260e] mb-10 transition-colors" data-testid="back-to-category-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> {t("All products")}
      </button>

      {/* Title */}
      <div className="mb-10">
        <span className="cchbc-eyebrow mb-6">{t("Digital Menu — QR · Tablet · Stand")}</span>
        <h1 className="font-condensed text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-5 leading-[0.9]" data-testid="product-title">Digital Menu</h1>
        <div className="flex items-center gap-2 mb-5">
          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#dd260e] border border-[#dd260e]/30 px-2 py-1 rounded-sm" data-testid="tag-digital-menu">{t("Digital Menu")}</span>
          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#555555] border border-[#e8e8e8] px-2 py-1 rounded-sm" data-testid="tag-software">{t("Software")}</span>
        </div>
        <p className="text-[#555555] text-base sm:text-lg leading-relaxed max-w-2xl">
          {t("One menu — three ways to access it.")} <strong className="text-[#000000] font-semibold">{t("QR (BYOD)")}</strong>, <strong className="text-[#000000] font-semibold">{t("Tablet")}</strong> {t("or a")} <strong className="text-[#000000] font-semibold">{t("Digital Stand")}</strong> {t("at the entrance. Central CMS, multilingual, allergens, real-time updates.")}
        </p>
      </div>

      {/* Three delivery modes */}
      <div className="mb-14" data-testid="formats-section">
        <span className="cchbc-eyebrow mb-6">{t("Three delivery modes")}</span>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {/* QR Mode */}
          <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden hover:border-[#000000] transition-colors flex flex-col" data-testid="format-qr-card">
            <div className="h-48 bg-[#fafafa] overflow-hidden flex items-center justify-center p-4">
              <img src="/qr-scan-phone.png" alt="QR Menu — guest scanning with phone" className="max-h-full max-w-full object-contain" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <span className="font-condensed text-xs font-black tracking-tight text-white bg-[#dd260e] px-2 py-0.5 rounded-sm inline-block mb-3 self-start">Mode 01</span>
              <h3 className="font-condensed text-2xl font-black uppercase tracking-tight text-[#000000] mb-2">{t("QR Menu")}</h3>
              <p className="text-sm text-[#555555] leading-relaxed mb-4 flex-1">{t("Guests scan a QR with their own phone (BYOD). Zero hardware, instant rollout.")}</p>
              <a href="https://qr.master-fb.com/6JC6ES7X" target="_blank" rel="noopener noreferrer" className="cchbc-link" data-testid="format-qr-demo">
                <ExternalLink className="w-3.5 h-3.5" /> {t("Try QR live demo")}
              </a>
            </div>
          </div>

          {/* Tablet Mode */}
          <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden hover:border-[#000000] transition-colors flex flex-col" data-testid="format-tablet-card">
            <div className="h-48 bg-black overflow-hidden">
              <img src="/tablet-menu-hero.jpg" alt="Tablet Menu with FORA-branded Croatian menu" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <span className="font-condensed text-xs font-black tracking-tight text-white bg-[#dd260e] px-2 py-0.5 rounded-sm inline-block mb-3 self-start">Mode 02</span>
              <h3 className="font-condensed text-2xl font-black uppercase tracking-tight text-[#000000] mb-2">{t("Tablet Menu")}</h3>
              <p className="text-sm text-[#555555] leading-relaxed mb-4 flex-1">{t("Samsung Galaxy Tab on the table with touch UI and upsell prompts.")}</p>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555]" data-testid="format-tablet-spec">
                {t("Samsung Galaxy Tab · Touch UI")}
              </span>
            </div>
          </div>

          {/* Stand Mode */}
          <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden hover:border-[#000000] transition-colors flex flex-col" data-testid="format-stand-card">
            <div className="h-48 overflow-hidden bg-black">
              <img src="/digital-stand-hero.jpg" alt="Digital stand kiosk at restaurant entrance" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <span className="font-condensed text-xs font-black tracking-tight text-white bg-[#dd260e] px-2 py-0.5 rounded-sm inline-block mb-3 self-start">Mode 03</span>
              <h3 className="font-condensed text-2xl font-black uppercase tracking-tight text-[#000000] mb-2">{t("Digital Stand")}</h3>
              <p className="text-sm text-[#555555] leading-relaxed mb-4 flex-1">{t("Touch screen kiosk at the entrance for self-service browsing.")}</p>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555]" data-testid="format-stand-spec">
                {t("Floor / counter kiosk · Touch UI")}
              </span>
            </div>
          </div>
        </div>
      </div>


      {/* Section 1: WHY SHOULD I CARE — Benefit numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 mb-14 border-t border-b border-[#e8e8e8]" data-testid="benefit-numbers">
        {benefits.map((b, i) => (
          <div key={b.label} className={`bg-white p-6 sm:p-7 ${i < benefits.length - 1 ? "border-b lg:border-b-0 lg:border-r border-[#e8e8e8]" : ""} ${i === 1 ? "border-b lg:border-b-0" : ""}`}>
            <b.icon className="w-5 h-5 text-[#dd260e] mb-4" strokeWidth={2} />
            <p className="font-condensed text-4xl font-black uppercase tracking-tight text-[#000000]">{b.value}</p>
            <p className="text-[10px] text-[#555555] mt-2 leading-snug uppercase tracking-[0.1em] font-semibold">{t(b.label)}</p>
          </div>
        ))}
      </div>

      {/* Section 2: HOW DO I GET IT — CTAs + live demo */}
      <div className="bg-[#000000] rounded-sm p-8 sm:p-10 mb-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="get-it-section">
        <div>
          <span className="cchbc-eyebrow light mb-3">{t("Try it live")}</span>
          <p className="font-condensed text-white font-black text-2xl uppercase tracking-tight mt-3 mb-2">{t("See it in action")}</p>
          <p className="text-white/60 text-sm max-w-md">
            {t("Try a live QR menu demo or schedule a call with the FORA team")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 shrink-0">
          <a href="https://qr.master-fb.com/6JC6ES7X" target="_blank" rel="noopener noreferrer" className="cchbc-link light" data-testid="live-demo-btn">
            <ExternalLink className="w-3.5 h-3.5" /> {t("Live Demo")}
          </a>
          <a
            href={`mailto:martina.lovetinska@cchellenic.com,pero.pehar@master-fb.com,igor.guberovic@cchellenic.com,katarzyna.stanczak@cchellenic.com?subject=${encodeURIComponent("FORA Digital Solutions — Schedule a meeting")}&body=${encodeURIComponent("Hi team,\n\nI'd like to schedule a meeting to discuss FORA Digital Solutions for our market.\n\nProduct of interest: Digital Menu\n\nPlease suggest a few available time slots.\n\nBest regards")}`}
            className="cchbc-btn cchbc-btn-outline-light"
            data-testid="schedule-meeting-btn"
          >
            <Calendar className="w-3.5 h-3.5" /> Schedule call
          </a>
        </div>
      </div>

      {/* Product Gallery */}
      <div className="mb-8" data-testid="product-gallery">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-2 auto-rows-[100px] sm:auto-rows-[140px]">
          <div className="col-span-2 md:col-span-7 row-span-2 rounded-sm overflow-hidden relative group"><img src="/qr-order-pay.png" alt="Order & Pay" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
          <div className="col-span-1 md:col-span-5 rounded-sm overflow-hidden"><img src="/qr-phone-menu.png" alt="QR Menu" className="w-full h-full object-cover" /></div>
          <div className="col-span-1 md:col-span-5 rounded-sm overflow-hidden bg-[#000000]"><img src="/qr-code-cocacola.png" alt="QR code" className="w-full h-full object-contain p-2" /></div>
          <div className="col-span-1 md:col-span-5 rounded-sm overflow-hidden bg-black"><img src="/qr-menu-devices.jpg" alt="QR Menu devices showcase" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" /></div>
          <div className="col-span-1 md:col-span-7 rounded-sm overflow-hidden"><img src="/qr-scan-phone.png" alt="Scanning" className="w-full h-full object-cover" /></div>
        </div>
      </div>

      {/* Features + Dependencies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-14 border-t border-[#e8e8e8]">
        <div className="bg-white p-8 lg:border-r border-b lg:border-b-0 border-[#e8e8e8]" data-testid="key-features-card">
          <span className="cchbc-eyebrow mb-6">{t("Key Features")}</span>
          <ul className="space-y-4 mt-6">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-4 pb-4 border-b border-[#e8e8e8] last:border-b-0 last:pb-0">
                <f.icon className="w-5 h-5 text-[#dd260e] shrink-0 mt-0.5" strokeWidth={1.8} />
                <span className="text-sm text-[#000000] font-medium">{t(f.text)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white p-8" data-testid="dependencies-card">
          <span className="cchbc-eyebrow mb-3">{t("Dependencies & Linked Products")}</span>
          <p className="text-sm text-[#555555] leading-relaxed max-w-md mt-4 mb-6">
            {t("FORA ecosystem combines ordering, payments, and data into a single scalable platform.")}
          </p>
          <div className="flex flex-wrap gap-2 mb-8">
            {dependencies.map((d, idx) => (
              <Popover
                key={d.name}
                open={activeDep === idx}
                onOpenChange={(o) => setActiveDep(o ? idx : null)}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onMouseEnter={() => openDep(idx)}
                    onMouseLeave={scheduleCloseDep}
                    aria-label={`${d.name} \u2014 details`}
                    className="group inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#e8e8e8] text-[#000000] text-xs font-medium rounded-sm hover:border-[#000000] focus:border-[#dd260e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#dd260e]/30 transition-colors cursor-pointer"
                    data-testid={`dependency-tag-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                  >
                    {d.name}
                    <span className="w-1 h-1 rounded-full bg-[#dd260e] group-hover:scale-125 transition-transform" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  collisionPadding={16}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                  onMouseEnter={() => openDep(idx)}
                  onMouseLeave={scheduleCloseDep}
                  className="w-[min(320px,calc(100vw-32px))] border-[#000000] bg-[#000000] text-white p-4 rounded-sm shadow-xl"
                  data-testid={`dependency-popover-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#dd260e] mb-2">{d.name}</p>
                  <p className="text-xs text-white/80 leading-relaxed">{t(d.desc)}</p>
                </PopoverContent>
              </Popover>
            ))}
          </div>
          <span className="cchbc-eyebrow mb-4">{t("Active in Markets")}</span>
          <div className="flex flex-wrap gap-2 mb-6 mt-4">
            {markets.map((m) => (<span key={m.code} className="px-2.5 py-1 bg-[#000000] text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-sm">{m.code}</span>))}
          </div>
          <button onClick={() => onNavigate("countries")} className="cchbc-link" data-testid="see-country-examples-link">
            {t("See country examples")} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* QR Themes */}
      <div className="mb-14" data-testid="themes-section">
        <div className="flex items-end justify-between mb-6 pb-4 border-b border-[#e8e8e8]">
          <div>
            <span className="cchbc-eyebrow mb-3">{t("7 Templates")}</span>
            <h2 className="font-condensed text-3xl font-black uppercase tracking-tight text-[#000000] mt-3">{t("QR Menu Themes")}</h2>
            <p className="text-sm text-[#555555] mt-2">{t("Pre-designed Coca-Cola branded themes. Click to preview.")}</p>
          </div>
          <Palette className="w-6 h-6 text-[#dd260e] hidden sm:block" strokeWidth={1.5} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {themes.map((t2) => (
            <a key={t2.name} href={t2.url} target="_blank" rel="noopener noreferrer" className="bg-white border border-[#e8e8e8] rounded-sm p-5 hover:border-[#000000] transition-colors group text-center" data-testid={`theme-${t2.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-center gap-1.5 mb-4">
                {t2.colors.map((c, i) => (<div key={i} className="w-6 h-6 rounded-sm border border-[#e8e8e8]" style={{ backgroundColor: c }} />))}
              </div>
              <p className="font-condensed text-sm font-bold uppercase tracking-tight text-[#000000] group-hover:text-[#dd260e] transition-colors mb-2">{t2.name}</p>
              <div className="flex items-center justify-center gap-1 text-[9px] text-[#555555] uppercase tracking-[0.15em] font-semibold"><ExternalLink className="w-3 h-3" /> {t("Demo")}</div>
            </a>
          ))}
        </div>
      </div>

      {/* Editable Elements — Visual showcase */}
      <div className="mb-14" data-testid="editable-elements-section">
        <div className="mb-8 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">{t("CMS Customization")}</span>
          <h2 className="font-condensed text-3xl font-black uppercase tracking-tight text-[#000000] mt-3">{t("What You Can Customize")}</h2>
          <p className="text-sm text-[#555555] mt-2">{t("Every aspect of the QR menu is editable from the CMS.")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { num: "01", title: "Popup Ad", image: "/edit-popup.png", desc: "Full-screen promotional overlay with custom image, logo, and text." },
            { num: "02", title: "Banner", image: "/edit-banner.png", desc: "Rotating top banner with brand imagery and CTAs." },
            { num: "03", title: "Client Branding", image: "/edit-client.png", desc: "Venue name, logo, type, Wi-Fi, and legal links." },
            { num: "04", title: "Top Info", image: "/edit-topinfo.png", desc: "Splash screen with custom welcome message." },
            { num: "05", title: "Bottom Info", image: "/edit-bottominfo.png", desc: "Wi-Fi, NIP, policy links, and navigation." },
            { num: "06", title: "Product Image & Video", image: "/edit-product.png", desc: "Photos, video, allergens, prices per item." },
            { num: "07", title: "Product List", image: "/edit-productlist.png", desc: "Pack shots, names, pricing, quantity controls.", note: "Assortment and category management (product order, category placement) is delivered by the Coca-Cola HBC BU country owner." },
            { num: "08", title: "Category Image", image: "/edit-category.png", desc: "Category banners and tab navigation." },
          ].map((section) => (
            <div key={section.num} className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden hover:border-[#000000] transition-all group" data-testid={`editable-section-${section.num}`}>
              <div className="h-52 overflow-hidden bg-gradient-to-b from-[#f5f5f5] to-[#e8e8e8] flex items-center justify-center p-4 relative">
                <img src={section.image} alt={section.title} className="max-h-full max-w-[75%] object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-xl" />
                <div className="absolute top-4 left-4">
                  <span className="font-condensed text-xs font-black tracking-tight text-white bg-[#dd260e] px-2 py-0.5 rounded-sm">{section.num}</span>
                </div>
              </div>
              <div className="p-5">
                <p className="font-condensed text-lg font-bold uppercase tracking-tight text-[#000000] mb-1 group-hover:text-[#dd260e] transition-colors">{t(section.title)}</p>
                <p className="text-[11px] text-[#555555] leading-relaxed">{t(section.desc)}</p>
                {section.note && (
                  <p className="text-[10px] text-[#dd260e] leading-relaxed mt-3 pt-3 border-t border-[#e8e8e8] italic">{t(section.note)}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <a href="/editable-elements.pdf" download className="cchbc-link mt-6">
          <Download className="w-3.5 h-3.5" /> {t("Download Editable Elements guide")}
        </a>
      </div>

      {/* Collapsible Implementation Details */}
      <div className="bg-white border border-[#e8e8e8] rounded-sm mb-10" data-testid="implementation-details">
        <button onClick={() => setImplOpen(!implOpen)} className="w-full flex items-center justify-between p-6 text-left hover:bg-[#fafafa] transition-colors" data-testid="toggle-impl-details">
          <div className="flex items-center gap-4">
            <FileText className="w-4 h-4 text-[#555555]" strokeWidth={1.8} />
            <span className="font-condensed text-lg font-bold uppercase tracking-tight text-[#000000]">{t("Implementation Details & Resources")}</span>
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#555555] border border-[#e8e8e8] px-2 py-0.5 rounded-sm">{documents.length} {t("docs")}</span>
          </div>
          {implOpen ? <ChevronUp className="w-5 h-5 text-[#555555]" /> : <ChevronDown className="w-5 h-5 text-[#555555]" />}
        </button>

        {implOpen && (
          <div className="border-t border-[#e8e8e8] p-6">
            {/* Image specs */}
            <span className="cchbc-eyebrow mb-4">{t("Image & Video Specs")}</span>
            <div className="bg-[#fafafa] border border-[#e8e8e8] rounded-sm overflow-x-auto mb-8 mt-4">
              <div className="min-w-[500px]">
                <div className="grid grid-cols-[1fr_1fr_1fr_80px] bg-[#000000] text-white">
                  <div className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em]">Type</div>
                  <div className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em]">Format</div>
                  <div className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em]">Resolution</div>
                  <div className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em]">Ratio</div>
                </div>
                {imageSpecs.map((s, i) => (
                  <div key={i} className={`grid grid-cols-[1fr_1fr_1fr_80px] ${i < imageSpecs.length - 1 ? "border-b border-[#e8e8e8]" : ""}`}>
                    <div className="px-4 py-3 text-xs font-medium text-[#000000] flex items-center gap-2">{s.type.includes("Video") ? <Film className="w-3 h-3 text-[#555555]" /> : <Image className="w-3 h-3 text-[#555555]" />} {s.type}</div>
                    <div className="px-4 py-3 text-xs text-[#555555]">{s.format}</div>
                    <div className="px-4 py-3 text-xs text-[#555555]">{s.resolution}</div>
                    <div className="px-4 py-3 text-xs text-[#555555]">{s.ratio}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents */}
            <span className="cchbc-eyebrow mb-4">{t("Documents & Resources")}</span>
            <div className="grid grid-cols-1 gap-3 mt-4">
              {documents.map((doc) => (
                <a key={doc.name} href={doc.file} download className="flex items-start gap-4 bg-white border border-[#e8e8e8] rounded-sm p-5 hover:border-[#000000] transition-colors group" data-testid={`doc-${doc.name.toLowerCase().replace(/[\s&()—]+/g, "-")}`}>
                  <FileText className="w-5 h-5 text-[#dd260e] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-bold text-[#000000] group-hover:text-[#dd260e] transition-colors">{t(doc.name)}</p>
                      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#555555] border border-[#e8e8e8] px-1.5 py-0.5 rounded-sm">{doc.type}</span>
                    </div>
                    <p className="text-xs text-[#555555] leading-relaxed">{t(doc.desc)}</p>
                  </div>
                  <Download className="w-4 h-4 text-[#e8e8e8] group-hover:text-[#dd260e] transition-colors shrink-0 mt-0.5" />
                </a>
              ))}
            </div>

            {/* Service description */}
            <div className="mt-6 p-5 bg-[#fafafa] border border-[#e8e8e8] rounded-sm">
              <span className="cchbc-eyebrow mb-3">{t("Service Description")}</span>
              <p className="text-sm text-[#555555] leading-relaxed mt-3">
                {t("Platform activation, menu structure configuration and QR link publishing. Includes CCHBC assortment writing standard setup, category image configuration, product packshot integration, and multilingual content deployment.")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* CTA Footer */}
      <div className="pt-10 border-t border-[#e8e8e8]" data-testid="cta-footer">
        <span className="cchbc-eyebrow mb-6">{t("Next Steps")}</span>
        <div className="flex flex-wrap items-center gap-6 mt-6">
          <a
            href={`mailto:martina.lovetinska@cchellenic.com,pero.pehar@master-fb.com,igor.guberovic@cchellenic.com,katarzyna.stanczak@cchellenic.com?subject=${encodeURIComponent("FORA Digital Solutions — Schedule a meeting")}&body=${encodeURIComponent("Hi team,\n\nI'd like to schedule a meeting to discuss FORA Digital Solutions for our market.\n\nProduct of interest: Digital Menu\n\nPlease suggest a few available time slots.\n\nBest regards")}`}
            className="cchbc-btn cchbc-btn-primary"
            data-testid="schedule-meeting-btn-bottom"
          >
            <Calendar className="w-3.5 h-3.5" /> {t("Schedule a meeting")}
          </a>
          <button
            type="button"
            onClick={() => {
              const original = document.title;
              document.title = "FORA-Digital-Menu-Product-Brief";
              window.print();
              setTimeout(() => { document.title = original; }, 200);
            }}
            className="cchbc-link"
            data-testid="download-pdf-btn"
          >
            <Download className="w-3.5 h-3.5" /> {t("Download product brief (PDF)")}
          </button>
        </div>
      </div>

      <PageFooter />
    </div>
  );
};
